import { fetchWithTimeout } from '../utils';

function parseDejavooMoney(value) {
  if (value == null || value === '') return NaN;
  const n = parseFloat(String(value).replace(/,/g, ''), 10);
  return Number.isFinite(n) ? n : NaN;
}

function firstExtendedAppBlock(data) {
  const byApp = data && data.ExtendedDataByApplication;
  if (!byApp || typeof byApp !== 'object') return null;
  const vals = Object.values(byApp);
  return vals.length ? vals[0] : null;
}

/**
 * Approved/charged sale amount for partial or full Dejavoo credit responses.
 *
 * On partial approvals, the top-level `Amounts.ApprovedAmount` has been observed
 * (in production terminal responses) to incorrectly echo the requested
 * `Amounts.Amount` rather than the amount actually approved. When the terminal
 * reports a partial approval, `ExtendedDataByApplication.<app>.ApprovedAmnt` is
 * the reliable field, so it's checked first. It's only populated on partial
 * approvals, so this is a no-op for full approvals (including surcharge ones).
 */
export function getDejavooApprovedSaleAmount(data) {
  const amounts = data.Amounts || {};
  const ext = firstExtendedAppBlock(data);
  if (ext) {
    const extN = parseDejavooMoney(ext.ApprovedAmnt != null ? ext.ApprovedAmnt : ext.ApprovedAmount);
    if (Number.isFinite(extN) && extN >= 0) return extN;
  }
  let n = parseDejavooMoney(amounts.ApprovedAmount);
  if (Number.isFinite(n) && n > 0) return n;
  n = parseDejavooMoney(amounts.Amount);
  if (Number.isFinite(n) && n >= 0) return n;
  if (ext) {
    n = parseDejavooMoney(ext.Amount);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return NaN;
}

/** Terminal-applied fee/surcharge from a Dejavoo sale response (when POS has no surcharge configured). */
export function getDejavooFeeAmount(data) {
  if (!data || typeof data !== 'object') return 0;
  const amounts = data.Amounts || {};
  let n = parseDejavooMoney(amounts.FeeAmount);
  if (Number.isFinite(n) && n > 0) return n;
  const ext = firstExtendedAppBlock(data);
  if (ext) {
    n = parseDejavooMoney(ext.Fee);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function isDejavooPartialApprovalResponse(data, statusCode) {
  if (String(data.IsPartialApprovalTxn).toLowerCase() === 'true') return true;
  const ext = firstExtendedAppBlock(data);
  if (ext && String(ext.IsPartialApprovalTxn).toLowerCase() === 'true') return true;
  return statusCode === '0001';
}

/** User-facing copy for failed sale/refund/void bodies (HTTP may still be 200). */
function getDejavooFailureUserMessage(data, fallbackMessage) {
  const fallback = fallbackMessage || 'Payment not approved.';
  if (!data || typeof data !== 'object') return fallback;
  const gr = data.GeneralResponse;
  if (!gr || typeof gr !== 'object') return fallback;

  const status = String(gr.StatusCode ?? '');
  const detailed = String(gr.DetailedMessage || '').trim();
  const message = String(gr.Message || '').trim();
  const hostMsg = String(gr.HostResponseMessage || '').replace(/%20/g, ' ').trim();
  const ext = firstExtendedAppBlock(data);
  const declineReason = ext && ext.DeclineReason != null ? String(ext.DeclineReason).trim() : '';

  if (
    status === '2001' ||
    /Terminal not connected to SPIn Proxy/i.test(detailed) ||
    /not connected to SPIn Proxy/i.test(message) ||
    /not connected to SPIn Proxy/i.test(hostMsg)
  ) {
    return 'Payment terminal is currently unreachable. Please verify that it’s powered on and connected to the internet. If it’s already connected, a quick restart of the Dejavoo terminal should resolve the issue.';
  }

  const detailLine = declineReason || detailed || hostMsg || message;
  const declineByStatus = status === '1012';
  const declineByText =
    /declin/i.test(declineReason) ||
    /declin/i.test(detailed) ||
    /declin/i.test(hostMsg) ||
    (message === 'Canceled' && (declineReason || /declin/i.test(detailed) || /declin/i.test(hostMsg)));

  if (declineByStatus || declineByText) {
    if (detailLine) {
      return `Card is declined. ${detailLine}`;
    }
    return 'Card is declined.';
  }

  if (detailLine) return detailLine;
  return fallback;
}

/**
 * Build void request fields from a sale/approval JSON object or `processor_response` string.
 * Uses ReferenceId, PaymentType, and captured amount from the gateway response (not the client request).
 */
export function getDejavooVoidParamsFromSaleResponse(dataOrJson) {
  let parsed = dataOrJson;
  if (typeof dataOrJson === 'string') {
    if (!dataOrJson || dataOrJson === 'no data') return null;
    try {
      parsed = JSON.parse(dataOrJson);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object') return null;

  const referenceId = parsed.ReferenceId;
  if (referenceId == null || referenceId === '') return null;

  const paymentType = parsed.PaymentType;
  if (!paymentType) return null;

  const amounts = parsed.Amounts || {};
  const ext = firstExtendedAppBlock(parsed);
  let amount = ext
    ? parseDejavooMoney(ext.ApprovedAmnt != null ? ext.ApprovedAmnt : ext.ApprovedAmount)
    : NaN;
  if (!Number.isFinite(amount) || amount <= 0) {
    amount = parseDejavooMoney(amounts.ApprovedAmount);
  }
  if (!Number.isFinite(amount) || amount <= 0) {
    amount = parseDejavooMoney(amounts.Amount);
  }
  if (!Number.isFinite(amount) || amount <= 0) {
    amount = ext ? parseDejavooMoney(ext.Amount) : NaN;
  }
  if (!Number.isFinite(amount) || amount <= 0) return null;

  return {
    referenceId: String(referenceId),
    paymentType: String(paymentType),
    amount: Number(amount.toFixed(2))
  };
}

/** JSON snapshot of a Dejavoo API response for `processor_response` on payments. Omits `_requestPayload` (client-only; includes Tpn/Authkey). */
export function serializeDejavooProcessorResponse(data) {
  if (data == null || typeof data !== 'object') return 'no data';
  try {
    const clone = JSON.parse(JSON.stringify(data));
    delete clone._requestPayload;
    return JSON.stringify(clone);
  } catch (e) {
    console.warn('serializeDejavooProcessorResponse failed', e);
    return 'no data';
  }
}

export const parseDejavooConfig = (urlField) => {
  // Add null/undefined check with user-friendly error message
  if (!urlField || typeof urlField !== 'string') {
    throw new Error('The payment type is not configured on the Dashboard.');
  }

  const [test, tpn, authkey] = urlField.split(',').map(item => item.trim());
  const baseUrl = test.toLowerCase() === 'test'
    ? process.env.VUE_APP_DEJAVOO_TEST_URL
    : process.env.VUE_APP_DEJAVOO_PROD_URL;

  return {
    baseUrl,
    tpn,
    authkey
  };
};

export const makeDejavooPayment = async (urlField, amount, referenceId, invoiceNumber, transaction_type) => {
  try {
    // TEST MODE: If transaction_type is "TestPartial", simulate a partial approval response
    if (transaction_type === "TestPartial") {
      // Simulate a delay like a real API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Calculate partial approval amount (70% of requested amount for testing)
      const approvedAmount = (amount * 0.7).toFixed(2);

      // Return a mock partial approval response
      return {
        GeneralResponse: {
          StatusCode: "0001",
          Message: "Partial Approval",
          DetailedMessage: "Partial Approval"
        },
        Amounts: {
          ApprovedAmount: approvedAmount,
          Amount: Number(approvedAmount),
          TipAmount: "0.00",
          RequestedAmount: amount.toString()
        },
        CardData: {
          Last4: "1234",
          CardType: "VISA",
          EntryType: "Chip",
          First4: "4111",
          BIN: "411111",
          ExpirationDate: "12/25",
          Name: "TEST CARD"
        },
        AuthCode: "TEST123",
        TransactionType: "Sale",
        PaymentType: "Credit",
        ReferenceId: referenceId,
        IsPartialApprovalTxn: "true", // Added based on your feedback
        isPartialApproval: true // Internal flag for the UI
      };
    }

    const { baseUrl, tpn, authkey } = parseDejavooConfig(urlField);
    const headers = {
      'Content-Type': 'application/json'
    };

    // Check if this is a Gift payment type
    const isGiftPayment = transaction_type === "Gift";

    // Determine endpoint based on payment type
    // For Gift payments, replace /Payment with /Gift/Redeem in the baseUrl
    // For other payments, append /Sale to the baseUrl
    const endpoint = isGiftPayment
      ? baseUrl.replace('/Payment', '/Gift/Redeem')
      : `${baseUrl}/Sale`;

    // Build payload based on payment type
    let payload;
    if (isGiftPayment) {
      // Gift payment uses simplified payload structure
      payload = {
        TipAmount: "0.00",
        Amount: amount.toString(),
        ReferenceId: referenceId,
        GetReceipt: "No",
        PrintReceipt: "No",
        InvoiceNumber: invoiceNumber,
        GetExtendedData: true,
        CallbackInfo: {
          Url: ""
        },
        Tpn: tpn,
        Authkey: authkey,
        SPInProxyTimeout: 180,
        CustomFields: {}
      };
    } else {
      // Credit and other payment types use full payload structure
      payload = {
        Amount: amount.toString(),
        TipAmount: "0.00",
        ExternalReceipt: "",
        PaymentType: transaction_type,
        ReferenceId: referenceId,
        PrintReceipt: "No",
        GetReceipt: "No",
        MerchantNumber: "",
        InvoiceNumber: invoiceNumber,
        CaptureSignature: false,
        GetExtendedData: true,
        CallbackInfo: {
          Url: ""
        },
        Tpn: tpn,
        Authkey: authkey,
        SPInProxyTimeout: 180,
        CustomFields: {}
      };
    }

    const response = await fetchWithTimeout(300000, fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    }));

    const text = await response.text();

    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      console.error('Error parsing Dejavoo payment response:', parseError);
      throw new Error(`Error processing payment response. Status: ${response.status}`);
    }

    data._requestPayload = payload;

    if (!data.GeneralResponse) {
      throw new Error('Payment not approved - no response from the terminal.');
    }

    const statusCode = data.GeneralResponse.StatusCode;

    // Partial: StatusCode 0001 and/or IsPartialApprovalTxn in root or ExtendedDataByApplication.
    if (isDejavooPartialApprovalResponse(data, statusCode)) {
      data.isPartialApproval = true;
      const approved = getDejavooApprovedSaleAmount(data);
      if (Number.isFinite(approved)) {
        data.Amounts = data.Amounts || {};
        data.Amounts.ApprovedAmount = approved;
      }
      return data;
    }

    if (statusCode === '0000') {
      return data;
    }

    throw new Error(getDejavooFailureUserMessage(data, 'Payment not approved.'));
  } catch (error) {
    console.error('Error in Dejavoo payment:', error);
    throw error;
  }
};

export const makeDejavooRefund = async (urlField, amount, referenceId, invoiceNumber, transaction_type) => {
  try {
    const { baseUrl, tpn, authkey } = parseDejavooConfig(urlField);
    const headers = {
      'Content-Type': 'application/json'
    };

    // Check if this is a Gift payment type
    const isGiftPayment = transaction_type === "Gift";

    // Determine endpoint based on payment type
    // For Gift refunds, replace /Payment with /Gift/Refund in the baseUrl
    // For other refunds, append /Return to the baseUrl
    const endpoint = isGiftPayment
      ? baseUrl.replace('/Payment', '/Gift/Refund')
      : `${baseUrl}/Return`;

    // Build payload based on payment type
    let payload;
    if (isGiftPayment) {
      // Gift refund uses simplified payload structure
      payload = {
        Amount: Math.abs(amount).toString(),
        ReferenceId: referenceId,
        GetReceipt: "No",
        PrintReceipt: "No",
        InvoiceNumber: invoiceNumber,
        GetExtendedData: true,
        CallbackInfo: {
          Url: ""
        },
        Tpn: tpn,
        Authkey: authkey,
        SPInProxyTimeout: 180,
        CustomFields: {}
      };
    } else {
      // Credit and other payment types use full payload structure
      payload = {
        Amount: Math.abs(amount).toString(),
        PaymentType: transaction_type,
        ReferenceId: referenceId,
        PrintReceipt: "No",
        GetReceipt: "No",
        MerchantNumber: null,
        InvoiceNumber: invoiceNumber,
        CaptureSignature: false,
        GetExtendedData: true,
        CallbackInfo: {
          Url: ""
        },
        Tpn: tpn,
        Authkey: authkey,
        SPInProxyTimeout: 180,
        CustomFields: {}
      };
    }

    const response = await fetchWithTimeout(300000, fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    }));

    const text = await response.text();

    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      console.error('Error parsing Dejavoo refund response:', parseError);
      throw new Error(`Error processing refund response. Status: ${response.status}`);
    }

    data._requestPayload = payload;

    if (!data.GeneralResponse || data.GeneralResponse.StatusCode !== '0000') {
      throw new Error(getDejavooFailureUserMessage(data, 'Refund not approved.'));
    }

    return data;
  } catch (error) {
    console.error('Error in Dejavoo refund:', error);
    throw error;
  }
};

/**
 * Void a Dejavoo / SPIn sale. Use {@link getDejavooVoidParamsFromSaleResponse} with the stored sale `processor_response`.
 * @param {string} urlField - Dashboard config: "test|production, tpn, authkey"
 * @param {object} params
 * @param {number} params.amount - Captured sale amount (must match approved transaction)
 * @param {string} params.referenceId - ReferenceId from the original sale response
 * @param {string} params.paymentType - PaymentType from the original sale response (e.g. "Credit")
 * @param {string} [params.integrationTransactionType] - Dashboard transaction_type; "TestPartial" skips the live API
 */
export const makeDejavooVoid = async (urlField, params) => {
  const { amount, referenceId, paymentType, integrationTransactionType } = params || {};

  try {
    if (integrationTransactionType === 'TestPartial') {
      await new Promise(resolve => setTimeout(resolve, 1500));
      return {
        GeneralResponse: {
          StatusCode: '0000',
          Message: 'Approved',
          DetailedMessage: 'Void Approved'
        },
        ReferenceId: referenceId,
        TransactionType: 'Void'
      };
    }

    if (referenceId == null || referenceId === '') {
      throw new Error('Void requires ReferenceId from the original transaction.');
    }
    if (!paymentType) {
      throw new Error('Void requires PaymentType from the original transaction.');
    }
    const voidAmount = Number(parseFloat(String(amount)).toFixed(2));
    if (!Number.isFinite(voidAmount) || voidAmount <= 0) {
      throw new Error('Void requires a valid transaction amount.');
    }

    const { baseUrl, tpn, authkey } = parseDejavooConfig(urlField);
    const headers = {
      'Content-Type': 'application/json'
    };

    const payload = {
      Amount: voidAmount,
      PaymentType: paymentType,
      ReferenceId: String(referenceId),
      PrintReceipt: 'No',
      GetReceipt: 'No',
      MerchantNumber: null,
      CaptureSignature: false,
      GetExtendedData: true,
      CallbackInfo: {
        Url: ''
      },
      Tpn: tpn,
      Authkey: authkey,
      SPInProxyTimeout: null,
      CustomFields: {}
    };

    const response = await fetchWithTimeout(300000, fetch(`${baseUrl}/Void`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    }));

    const text = await response.text();

    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      console.error('Error parsing void response:', parseError);
      throw new Error(`Error processing void response. Status: ${response.status}`);
    }

    data._requestPayload = payload;

    if (!data.GeneralResponse || data.GeneralResponse.StatusCode !== '0000') {
      const errorMessage = data.GeneralResponse?.DetailedMessage || data.GeneralResponse?.Message || 'Void not approved';
      throw new Error(errorMessage);
    }

    return data;
  } catch (error) {
    console.error('Error in Dejavoo void:', error);
    throw error;
  }
};

/**
 * Tells the terminal to stop the transaction currently in flight (user-initiated cancel while
 * waiting on the card tap/swipe). This does not cancel the client-side Sale/Refund fetch, which
 * is left to resolve/reject/time out on its own; callers should ignore a late response once the
 * user has cancelled.
 */
export const makeDejavooAbortTransaction = async (urlField) => {
  try {
    const { baseUrl, tpn, authkey } = parseDejavooConfig(urlField);
    const headers = {
      'Content-Type': 'application/json'
    };

    const payload = {
      Tpn: tpn,
      Authkey: authkey,
      SPInProxyTimeout: 180,
      CustomFields: {}
    };

    const response = await fetchWithTimeout(15000, fetch(`${baseUrl}/AbortTransaction`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    }));

    const text = await response.text();

    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch (parseError) {
      console.error('Error parsing abort transaction response:', parseError);
      throw new Error(`Error processing abort transaction response. Status: ${response.status}`);
    }

    data._requestPayload = payload;
    return data;
  } catch (error) {
    console.error('Error in Dejavoo abort transaction:', error);
    throw error;
  }
};
